import { Link, useLocation } from 'react-router-dom';
import TuneIcon from '@mui/icons-material/Tune';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SettingsCategoryKey, SettingsMaster } from '@/config/settingsRegistry';
import { cn } from '@/utils/cn';
import { resolveNavIconForPath } from '@/utils/pageNavIcon';
import { getSettingsCategoryTheme } from './settingsCategoryTheme';

type Props = {
  categoryKey: SettingsCategoryKey;
  label: string;
  icon: SvgIconComponent;
  masters: SettingsMaster[];
  className?: string;
  id?: string;
};

export function SettingsCategoryCard({ categoryKey, label, icon: CategoryIcon, masters, className, id }: Props) {
  const location = useLocation();
  const theme = getSettingsCategoryTheme(categoryKey);
  const currentPath = location.pathname.split('?')[0].replace(/\/+$/, '');

  return (
    <article
      id={id}
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm',
        'dark:border-slate-800 dark:bg-slate-900',
        className,
      )}
    >
      <header
        className={cn(
          'flex items-center gap-2.5 border-b border-slate-100 px-4 py-3',
          'dark:border-slate-800',
          theme.headerBg,
        )}
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
            theme.iconBg,
            theme.iconText,
          )}
          aria-hidden
        >
          <CategoryIcon sx={{ fontSize: 18 }} />
        </span>
        <h2 className={cn('text-sm font-semibold tracking-tight', theme.headerText)}>{label}</h2>
      </header>

      <ul className="flex flex-1 flex-col gap-0.5 p-2">
        {masters.map((master) => {
          const isActive = currentPath === master.route;
          const ItemIcon = resolveNavIconForPath(master.route) ?? TuneIcon;

          return (
            <li key={master.route}>
              <Link
                to={master.route}
                className={cn(
                  'group flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
                  isActive
                    ? cn(theme.itemActive, theme.itemActiveText, 'font-medium')
                    : cn('text-slate-700 dark:text-slate-300', theme.itemHover),
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center transition-opacity',
                    isActive
                      ? theme.itemActiveIcon
                      : 'opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60',
                  )}
                  aria-hidden
                >
                  <ItemIcon sx={{ fontSize: 16 }} />
                </span>
                <span className="min-w-0 leading-snug">{master.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </article>
  );
}
